/**
 * Typed API client for the purpose-built web UI.
 *
 * Thin, typed method layer over ../gateway (the single HTTP funnel:
 * `credentials: "include"` session cookie, x-csrf-token injection from
 * the module store set at login, `{ok,…}` envelope unwrapping, 401
 * fan-out). One method per gateway route group; request/response types
 * come from @llm-wiki/shared (packages/shared/src/{api,config}.ts) —
 * never guessed.
 *
 * Screens import this as: `import { client } from "../api/client"`.
 */
import { API_PREFIX, ApiRoutes } from "@llm-wiki/shared"
import type {
  ApiToken,
  CancelResearchResponse,
  ConfigKey,
  ConfigScope,
  ConnectionTestRequest,
  ConnectionTestResponse,
  ConnectionTestTarget,
  CreateApiTokenRequest,
  CreateApiTokenResponse,
  CreateProjectRequest,
  CreateProjectResponse,
  CreateUserRequest,
  DeleteSourcesRequest,
  DeleteSourcesResponse,
  EmbeddingsReindexResponse,
  EmbeddingsStatsResponse,
  EnqueueIngestResponse,
  FileContentResponse,
  FilesResponse,
  FilesRoot,
  GetConfigResponse,
  GitRepoSource,
  GitRepoStatus,
  GitSyncResult,
  GraphNode,
  GraphResponse,
  IngestQueueActionRequest,
  IngestQueueResponse,
  LintListResponse,
  LintRunResponse,
  ListApiTokensResponse,
  ListUsersResponse,
  LoginResponse,
  MeResponse,
  MessageReference,
  ModelOverride,
  OpenProjectResponse,
  Project,
  ProjectsResponse,
  RecentProjectsResponse,
  ResolveReviewRequest,
  ReviewsResponse,
  ReviewStatusFilter,
  RotationStatusResponse,
  SearchRequest,
  SearchResponse,
  StartResearchResponse,
  Tenant,
  TenantQuotas,
  UpdateProjectRequest,
  UpdateUserRequest,
  UploadSourcesResponse,
  WritePageResponse,
  ReverifyResult,
  ReviewAct,
  ReviewLogEntry,
} from "@llm-wiki/shared"
import {
  api,
  buildUrl,
  gatewayFetch,
  GatewayError,
  login as gatewayLogin,
  logout as gatewayLogout,
  fetchMe,
} from "../gateway"

export { GatewayError }

/* ── Response shapes composed by route handlers (not in shared) ─────── */

/** GET /projects/:id/git-sources — `authTokenRef` is stripped server-side. */
export interface RedactedGitRepo extends Omit<GitRepoSource, "authTokenRef"> {
  hasAuthToken: boolean
}
export interface GitSourcesConfigResponse {
  ok: true
  projectId: string
  config: { enabled: boolean; repos: RedactedGitRepo[] }
}

export interface TenantsListResponse {
  ok: true
  tenants: Tenant[]
}
export interface TenantQuotasResponse {
  ok: true
  quotas: TenantQuotas | null
}

export type { ProjectsResponse, MeResponse }

/* ── Client ─────────────────────────────────────────────────────────── */

export const client = {
  /* ── auth ── */
  auth: {
    /** Sets the session cookie AND the module CSRF store. */
    login: (email: string, password: string): Promise<LoginResponse> =>
      gatewayLogin(email, password),
    logout: (): Promise<void> => gatewayLogout(),
    /** null when unauthenticated. Refreshes the CSRF token on success. */
    me: (): Promise<MeResponse | null> => fetchMe(),
    changePassword: (currentPassword: string, newPassword: string) =>
      api(ApiRoutes.changePassword(), {
        method: "POST",
        body: { currentPassword, newPassword },
      }),
    /** Own tokens; with `projectId`, the tokens restricted to that project. */
    listTokens: (opts?: { projectId?: string }): Promise<ListApiTokensResponse> =>
      api<ListApiTokensResponse>(ApiRoutes.apiTokens(), {
        query: { projectId: opts?.projectId },
      }),
    createToken: (body: CreateApiTokenRequest): Promise<CreateApiTokenResponse> =>
      api<CreateApiTokenResponse>(ApiRoutes.apiTokens(), { method: "POST", body }),
    revokeToken: (tokenId: string): Promise<{ ok: true; apiToken?: ApiToken }> =>
      api(ApiRoutes.apiToken(tokenId), { method: "DELETE" }),
  },

  /* ── projects ── */
  projects: {
    /** ProjectEntry list ({id,name,path,current}) + the caller's current. */
    list: (): Promise<ProjectsResponse> =>
      api<ProjectsResponse>(ApiRoutes.projects()),
    create: (body: CreateProjectRequest): Promise<CreateProjectResponse> =>
      api<CreateProjectResponse>(ApiRoutes.projects(), { method: "POST", body }),
    update: (
      projectId: string,
      body: UpdateProjectRequest,
    ): Promise<{ ok: true; project: Project }> =>
      api(ApiRoutes.project(projectId), { method: "PATCH", body }),
    /** Records per-user MRU; call when the user switches project. */
    open: (projectId: string): Promise<OpenProjectResponse> =>
      api<OpenProjectResponse>(ApiRoutes.openProject(projectId), { method: "POST" }),
    recents: (): Promise<RecentProjectsResponse> =>
      api<RecentProjectsResponse>(ApiRoutes.recents()),
  },

  /* ── config (per-key, scoped; see shared/src/config.ts CONFIG_KEYS) ── */
  config: {
    /** Resolved value (project > user > tenant); secrets redacted. */
    get: (
      key: ConfigKey,
      opts?: { projectId?: string; tenantId?: string },
    ): Promise<GetConfigResponse> =>
      api<GetConfigResponse>(ApiRoutes.config(key), { query: opts }),
    put: (
      key: ConfigKey,
      value: unknown,
      scope: ConfigScope,
      opts?: { projectId?: string; tenantId?: string },
    ): Promise<{ ok: true; key: string }> =>
      api(ApiRoutes.config(key), {
        method: "PUT",
        body: { value, scope, ...opts },
      }),
    delete: (
      key: ConfigKey,
      scope: ConfigScope,
      opts?: { projectId?: string; tenantId?: string },
    ): Promise<{ ok: true }> =>
      api(ApiRoutes.config(key), {
        method: "DELETE",
        query: { scope, ...opts },
      }),
  },

  /* ── files / pages ── */
  files: {
    /** Tree. root: 'wiki' (default) | 'sources' | 'raw' | 'raw/sources' | 'all'. */
    list: (
      projectId: string,
      opts?: { root?: FilesRoot; recursive?: boolean; maxFiles?: number },
    ): Promise<FilesResponse> =>
      api<FilesResponse>(ApiRoutes.files(projectId), {
        query: {
          root: opts?.root,
          recursive: opts?.recursive === false ? "false" : undefined,
          maxFiles: opts?.maxFiles,
        },
      }),
    /** Page/text content ({content}); 2 MB cap, text extensions only. */
    content: (projectId: string, path: string): Promise<FileContentResponse> =>
      api<FileContentResponse>(ApiRoutes.fileContent(projectId), {
        query: { path },
      }),
    /** Fenced page write (wiki editor). */
    writePage: (
      projectId: string,
      path: string,
      content: string,
    ): Promise<WritePageResponse> =>
      api<WritePageResponse>(ApiRoutes.writePage(projectId), {
        method: "POST",
        body: { path, content },
      }),
    /** Cascade delete wiki pages (embeddings + wikilink rewrite). */
    deletePages: (
      projectId: string,
      paths: string[],
    ): Promise<{ ok: true; deletedPaths: string[]; rewrittenFiles: number }> =>
      api(ApiRoutes.pagesDelete(projectId), { method: "POST", body: { paths } }),
    /** Binary media proxy URL for a markdown image ref (use as <img src>). */
    mediaUrl: (projectId: string, src: string): string =>
      buildUrl(ApiRoutes.media(projectId), { src }),
  },

  /* ── search ── */
  search: (projectId: string, body: SearchRequest): Promise<SearchResponse> =>
    api<SearchResponse>(ApiRoutes.search(projectId), { method: "POST", body }),

  /* ── graph ── */
  graph: (
    projectId: string,
    opts?: { q?: string; nodeType?: string; limit?: number },
  ): Promise<GraphResponse> =>
    api<GraphResponse>(ApiRoutes.graph(projectId), { query: opts }),
  /** One page's wikilinks in both directions (from the gateway graph cache). */
  graphLinks: (projectId: string, path: string): Promise<GraphLinksResponse> =>
    api<GraphLinksResponse>(`${ApiRoutes.graph(projectId)}/links`, { query: { path } }),

  /* ── sources ── */
  sources: {
    /**
     * Multipart upload into raw/sources/. Folder structure rides on the
     * File name (webkitdirectory sets it to the relative path); pass
     * `relPath` per file to override.
     */
    upload: (
      projectId: string,
      files: Array<{ file: File; relPath?: string }>,
    ): Promise<UploadSourcesResponse> => {
      const formData = new FormData()
      for (const { file, relPath } of files) {
        formData.append("file", file, relPath ?? file.name)
      }
      return api<UploadSourcesResponse>(ApiRoutes.uploadSources(projectId), {
        method: "POST",
        formData,
      })
    },
    /** Cascade delete of source files (+ derived wiki pages). */
    delete: (
      projectId: string,
      body: DeleteSourcesRequest,
    ): Promise<DeleteSourcesResponse> =>
      api<DeleteSourcesResponse>(ApiRoutes.deleteSources(projectId), {
        method: "POST",
        body,
      }),
  },

  /* ── ingest queue ── */
  ingest: {
    /** Snapshot; live updates via events(projectId) `queue.updated`. */
    queue: (projectId: string): Promise<IngestQueueResponse> =>
      api<IngestQueueResponse>(ApiRoutes.ingestQueue(projectId)),
    /** retry | cancel (need taskId) | retry-all-failed | cancel-all | clear-completed */
    action: (
      projectId: string,
      body: IngestQueueActionRequest,
    ): Promise<{ ok: true }> =>
      api(ApiRoutes.ingestActions(projectId), { method: "POST", body }),
    /** Enqueue source paths for (re-)ingest. */
    enqueue: (projectId: string, paths: string[]): Promise<EnqueueIngestResponse> =>
      api<EnqueueIngestResponse>(ApiRoutes.enqueueIngest(projectId), {
        method: "POST",
        body: { paths },
      }),
  },

  /* ── jobs (durable mirror + event log) ── */
  jobs: {
    list: (
      projectId: string,
      opts?: { kind?: string; status?: string; limit?: number },
    ): Promise<JobsListResponse> =>
      api<JobsListResponse>(ApiRoutes.jobs(projectId), {
        query: { kind: opts?.kind, status: opts?.status, limit: opts?.limit },
      }),
    events: (jobId: string, after = 0, limit = 500): Promise<JobEventsResponse> =>
      api<JobEventsResponse>(`${API_PREFIX}/jobs/${encodeURIComponent(jobId)}/events`, {
        query: { afterSeq: after, limit },
      }),
  },

  /* ── embeddings ── */
  embeddings: {
    stats: (projectId: string): Promise<EmbeddingsStatsResponse> =>
      api<EmbeddingsStatsResponse>(ApiRoutes.embeddingsStats(projectId)),
    reindex: (
      projectId: string,
      clearExisting?: boolean,
    ): Promise<EmbeddingsReindexResponse> =>
      api<EmbeddingsReindexResponse>(ApiRoutes.embeddingsReindex(projectId), {
        method: "POST",
        body: { clearExisting },
      }),
  },

  /* ── reviews ── */
  reviews: {
    list: (
      projectId: string,
      opts?: { status?: ReviewStatusFilter; type?: string; limit?: number },
    ): Promise<ReviewsResponse> =>
      api<ReviewsResponse>(ApiRoutes.reviews(projectId), { query: opts }),
    resolve: (
      projectId: string,
      reviewId: string,
      body: ResolveReviewRequest,
    ): Promise<{ ok: true }> =>
      api(`${ApiRoutes.review(projectId, reviewId)}/resolve`, {
        method: "POST",
        body,
      }),
    /** Review redesign: apply a per-type decision (the runtime applies it). */
    act: (projectId: string, reviewId: string, act: ReviewAct): Promise<{ ok: true; entry: ReviewLogEntry }> =>
      api(`${ApiRoutes.review(projectId, reviewId)}/act`, { method: "POST", body: act }),
    reverify: (
      projectId: string,
    ): Promise<{ ok: true; started: boolean; running: boolean; llmAvailable: boolean; last: (ReverifyResult & { at?: number }) | null }> =>
      api(ApiRoutes.reviewsReverify(projectId), { method: "POST", body: {} }),
    reverifyStatus: (
      projectId: string,
    ): Promise<{ ok: true; running: boolean; last: (ReverifyResult & { at?: number }) | null }> =>
      api(ApiRoutes.reviewsReverify(projectId)),
    log: (projectId: string, limit = 200): Promise<{ ok: true; entries: ReviewLogEntry[] }> =>
      api(ApiRoutes.reviewsLog(projectId), { query: { limit } }),
    undo: (projectId: string, entryId: string): Promise<{ ok: true; entry: ReviewLogEntry }> =>
      api(ApiRoutes.reviewsUndo(projectId, entryId), { method: "POST", body: {} }),
  },

  /* ── credential rotation status ── */
  rotation: {
    status: (projectId: string): Promise<RotationStatusResponse> =>
      api<RotationStatusResponse>(ApiRoutes.rotationStatus(projectId)),
  },

  /* ── lint ── */
  lint: {
    list: (projectId: string): Promise<LintListResponse> =>
      api<LintListResponse>(ApiRoutes.lint(projectId)),
    /** Live results arrive via SSE `lint.updated`. */
    run: (projectId: string, semantic?: boolean): Promise<LintRunResponse> =>
      api<LintRunResponse>(ApiRoutes.lintRun(projectId), {
        method: "POST",
        body: { semantic },
      }),
  },

  /* ── research ── */
  research: {
    start: (
      projectId: string,
      topic: string,
      searchQueries?: string[],
    ): Promise<StartResearchResponse> =>
      api<StartResearchResponse>(ApiRoutes.research(projectId), {
        method: "POST",
        body: { topic, searchQueries },
      }),
    cancel: (projectId: string, taskId: string): Promise<CancelResearchResponse> =>
      api<CancelResearchResponse>(ApiRoutes.researchCancel(projectId, taskId), {
        method: "POST",
      }),
  },

  /* ── git sources ── */
  gitSources: {
    list: (projectId: string): Promise<GitSourcesConfigResponse> =>
      api<GitSourcesConfigResponse>(ApiRoutes.gitSources(projectId)),
    /** Upsert; pass authToken to (re)seal the repo's token server-side. */
    upsert: (
      projectId: string,
      repo: Omit<GitRepoSource, "authTokenRef">,
      authToken?: string,
    ): Promise<GitSourcesConfigResponse> =>
      api<GitSourcesConfigResponse>(ApiRoutes.gitSources(projectId), {
        method: "POST",
        body: { repo, ...(authToken !== undefined ? { authToken } : {}) },
      }),
    remove: (
      projectId: string,
      repoId: string,
    ): Promise<GitSourcesConfigResponse & { removedFiles: number }> =>
      api(ApiRoutes.gitSource(projectId, repoId), { method: "DELETE" }),
    /** Clone/fetch can be slow — the server allows up to 10 minutes. */
    sync: (
      projectId: string,
      repoId: string,
    ): Promise<{ ok: true; results: GitSyncResult[] }> =>
      api(ApiRoutes.gitSourceSync(projectId, repoId), { method: "POST" }),
    status: (
      projectId: string,
      repoId: string,
    ): Promise<{ ok: true; status: GitRepoStatus }> =>
      api(ApiRoutes.gitSourceStatus(projectId, repoId)),
  },

  /* ── connection tests (Settings "Test" buttons; runs server-side) ── */
  connectionTest: (
    target: ConnectionTestTarget,
    body?: ConnectionTestRequest,
  ): Promise<ConnectionTestResponse> =>
    api<ConnectionTestResponse>(ApiRoutes.connectionTest(target), {
      method: "POST",
      body: body ?? {},
    }),

  /* ── tenants (admin) ── */
  tenants: {
    list: (): Promise<TenantsListResponse> => api("/api/v1/tenants"),
    create: (body: {
      name: string
      planLimits?: Record<string, unknown>
      ownerUserId?: string
    }): Promise<{ ok: true; tenant: Tenant }> =>
      api("/api/v1/tenants", { method: "POST", body }),
    quotas: (tenantId: string): Promise<TenantQuotasResponse> =>
      api(`/api/v1/tenants/${encodeURIComponent(tenantId)}/quotas`),
    putQuotas: (
      tenantId: string,
      quotas: Omit<TenantQuotas, "tenantId">,
    ): Promise<{ ok: true }> =>
      api(`/api/v1/tenants/${encodeURIComponent(tenantId)}/quotas`, {
        method: "PUT",
        body: quotas,
      }),
    addMember: (
      tenantId: string,
      userId: string,
      role?: "owner" | "admin" | "member" | "viewer",
    ): Promise<{ ok: true }> =>
      api(`/api/v1/tenants/${encodeURIComponent(tenantId)}/members`, {
        method: "POST",
        body: { userId, role },
      }),
    removeMember: (tenantId: string, userId: string): Promise<{ ok: true }> =>
      api(
        `/api/v1/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}`,
        { method: "DELETE" },
      ),
  },

  /* ── users (platform admin) ── */
  users: {
    list: (): Promise<ListUsersResponse> => api<ListUsersResponse>("/api/v1/users"),
    create: (
      body: CreateUserRequest & { tenantId?: string; tenantRole?: string },
    ): Promise<{ ok: true; user: ListUsersResponse["users"][number] }> =>
      api("/api/v1/users", { method: "POST", body }),
    update: (userId: string, body: UpdateUserRequest): Promise<{ ok: true }> =>
      api(`/api/v1/users/${encodeURIComponent(userId)}`, {
        method: "PATCH",
        body,
      }),
    remove: (userId: string): Promise<{ ok: true }> =>
      api(`/api/v1/users/${encodeURIComponent(userId)}`, { method: "DELETE" }),
  },

  /* ── chat (SSE-over-POST; see the `chat` helper below) ── */
  chat,

  /* ── raw escape hatch (rare routes without a dedicated method) ── */
  raw: gatewayFetch,
}

/** GET /projects/:id/jobs — rows of the durable jobs mirror. */
export interface GraphLinksResponse {
  ok: true
  projectId: string
  /** Graph id (file stem); null while the graph is being built. */
  id: string | null
  /** The page as a graph node; null for a query page or a page whose stem another file owns. */
  page: GraphNode | null
  outgoing: GraphNode[]
  incoming: GraphNode[]
  /** First build of the graph still running (HTTP 202). */
  building?: boolean
  progress?: { done: number; total: number } | null
  stale?: boolean
}

export interface JobRow {
  id: string
  projectId: string
  kind: string
  status: string
  summary: unknown
  createdAt: string
  updatedAt: string
}
export interface JobsListResponse {
  ok: true
  projectId: string
  jobs: JobRow[]
}
/** GET /jobs/:id/events — the job's event log. */
export interface JobEventRow {
  jobId: string
  seq: number
  ts: string
  type: string
  data: unknown
}
export interface JobEventsResponse {
  ok: true
  jobId: string
  events: JobEventRow[]
}

/* ── SSE live updates ───────────────────────────────────────────────── */

/**
 * Per-project event stream (GET /projects/:id/events). Session cookie
 * auth — `withCredentials` matters when a dev origin differs.
 *
 * Event payloads are JSON `WikiSseEvent`s (shared/src/events.ts):
 * `data-version`, `file-tree`, `queue.updated`, `activity.added/updated`,
 * `job.updated`, `lint.updated`, `chat.*`, … Parse `event.data` and
 * discriminate on `.type`. Remember to `.close()` on unmount.
 *
 * @param types optional comma-separated type-prefix filter, e.g. "queue.,activity."
 */
export function events(projectId: string, types?: string): EventSource {
  return new EventSource(buildUrl(ApiRoutes.events(projectId), { types }), {
    withCredentials: true,
  })
}

/* ── Chat (SSE-over-POST) ────────────────────────────────────────────── */

/**
 * Normalized chat stream event — the wire carries `chat.accepted` (a bare
 * `{conversationId,streamId}` preamble) then `chat.{token,reasoning,
 * references,done,error}` frames wrapped as `{type,payload,at}`
 * (chat-routes.ts). `chat()` flattens both into this discriminated union
 * so screens never touch the envelope.
 */
export type ChatEvent =
  | { type: "chat.accepted"; conversationId: string; streamId: string }
  | { type: "chat.token"; token: string }
  | { type: "chat.reasoning"; text: string }
  | { type: "chat.references"; references: MessageReference[] }
  | { type: "chat.done"; content: string; references?: MessageReference[] }
  | { type: "chat.error"; error: string }

export interface ChatOptions {
  /** Continue an existing conversation (omit to start a new one). */
  conversationId?: string
  /** Mix external web search into retrieval (tenant searchApiConfig). */
  useWebSearch?: boolean
  /**
   * Chat-only provider+model override (model selector). Applies to this turn
   * only; the project's stored llmConfig (which also drives ingest) is
   * untouched. Omit → the project default.
   */
  modelOverride?: ModelOverride
  /** Abort to cancel the stream — the server cancels the run on disconnect. */
  signal?: AbortSignal
}

/** Parse a raw SSE `event`+`data` frame into a normalized ChatEvent. */
function normalizeChatFrame(eventName: string, data: unknown): ChatEvent | null {
  const d = (data ?? {}) as Record<string, unknown>
  const payload = (d.payload ?? {}) as Record<string, unknown>
  // `chat.accepted` carries fields at the top level; the rest wrap payload.
  const name = eventName || (typeof d.type === "string" ? d.type : "")
  switch (name) {
    case "chat.accepted":
      return {
        type: "chat.accepted",
        conversationId: String(d.conversationId ?? ""),
        streamId: String(d.streamId ?? ""),
      }
    case "chat.token":
      return { type: "chat.token", token: String(payload.token ?? "") }
    case "chat.reasoning":
      return { type: "chat.reasoning", text: String(payload.text ?? "") }
    case "chat.references":
      return {
        type: "chat.references",
        references: (payload.references as MessageReference[] | undefined) ?? [],
      }
    case "chat.done":
      return {
        type: "chat.done",
        content: String(payload.content ?? ""),
        references: payload.references as MessageReference[] | undefined,
      }
    case "chat.error":
      return { type: "chat.error", error: String(payload.error ?? "chat failed") }
    default:
      return null
  }
}

/**
 * Stream a chat turn. POSTs `{message,…}` to `/projects/:id/chat` and reads
 * the SSE response body (EventSource can't POST), invoking `onEvent` for
 * every normalized frame as tokens arrive. Resolves when the stream ends
 * (`chat.done`/`chat.error`/EOF); rejects with GatewayError on a non-SSE
 * error response (400/409/…). Abort via `opts.signal` to cancel.
 */
export async function chat(
  projectId: string,
  message: string,
  onEvent: (event: ChatEvent) => void,
  opts: ChatOptions = {},
): Promise<void> {
  const res = await gatewayFetch(ApiRoutes.chat(projectId), {
    method: "POST",
    body: {
      message,
      ...(opts.conversationId !== undefined ? { conversationId: opts.conversationId } : {}),
      ...(opts.useWebSearch !== undefined ? { useWebSearch: opts.useWebSearch } : {}),
      ...(opts.modelOverride !== undefined ? { modelOverride: opts.modelOverride } : {}),
    },
    headers: { Accept: "text/event-stream" },
    signal: opts.signal,
  })

  // Error responses come back as the JSON `{ok:false,error}` envelope.
  if (!res.ok || !res.body) {
    let msg = `${res.status} ${res.statusText}`
    let code: string | undefined
    try {
      const parsed = (await res.json()) as { error?: string; code?: string }
      if (parsed?.error) msg = parsed.error
      code = parsed?.code
    } catch {
      /* non-JSON body — keep the status line */
    }
    throw new GatewayError(msg, res.status, code)
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""

  const flushFrame = (raw: string): void => {
    let eventName = ""
    const dataLines: string[] = []
    for (const line of raw.split(/\r?\n/)) {
      if (line.startsWith("event:")) eventName = line.slice(6).trim()
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""))
      // `id:` / `:comment` / blank lines are ignored
    }
    if (dataLines.length === 0) return
    let data: unknown
    try {
      data = JSON.parse(dataLines.join("\n"))
    } catch {
      return
    }
    const normalized = normalizeChatFrame(eventName, data)
    if (normalized) onEvent(normalized)
  }

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      // SSE frames are separated by a blank line.
      let sep: number
      while ((sep = buffer.search(/\r?\n\r?\n/)) !== -1) {
        const frame = buffer.slice(0, sep)
        buffer = buffer.slice(sep + buffer.slice(sep).match(/^\r?\n\r?\n/)![0].length)
        if (frame.trim() !== "") flushFrame(frame)
      }
    }
    if (buffer.trim() !== "") flushFrame(buffer)
  } finally {
    reader.releaseLock()
  }
}

export type Client = typeof client
